/** The live strip on the open-position card: current premium, move vs entry,
 * unrealized P&L, and the INDEX that drives them.
 *
 * Split out of `OpenPositionCard` (2026-09-29) when the index prices pushed
 * that file past its line gate — the same reason `levels.ts` and
 * `QueuedExitBanner` were split out before it.
 *
 * ⚠️ Premium and index are formatted DIFFERENTLY on purpose: "Rs 143.85" for
 * the option, a bare "22,616.20" for the index. A premium and an index level
 * sitting in one row must never be mistakable for each other. */

import type { ReactElement } from "react";
import { underlyingOf } from "@/lib/openPosition";
import type { TradeLogEntry } from "@/types/tradeLog";
import { formatIndex } from "./levels";

/** The number the operator actually watches: current premium, move vs
 * entry, and unrealized P&L. Renders nothing until the first LTP tick. */
export function LiveStrip({
  trade,
  ltp,
  stale,
  spot,
}: Readonly<{
  trade: TradeLogEntry;
  ltp: number | null;
  stale: boolean;
  spot: number | null;
}>): ReactElement | null {
  if (ltp === null) return null;
  const entry = trade.entryPremium;
  const move = entry !== null ? ltp - entry : null;
  const pnl = move !== null && trade.quantity !== null ? move * trade.quantity : null;
  const tone = (v: number): string => {
    if (v > 0) return "text-success";
    if (v < 0) return "text-danger";
    return "text-ink-secondary";
  };
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg bg-surface-2 px-3 py-2">
      <span className="text-[10px] uppercase tracking-wider text-ink-secondary">Live</span>
      <span className="font-mono text-lg font-semibold">Rs {ltp.toFixed(2)}</span>
      {move !== null && (
        <span className={`font-mono text-sm ${tone(move)}`}>{signedPoints(move)} vs entry</span>
      )}
      {pnl !== null && (
        <span className={`font-mono text-sm font-medium ${tone(pnl)}`}>
          P&L {signedRupees(pnl)}
        </span>
      )}
      <SpotBadge trade={trade} spot={spot} />
      {stale && (
        <span className="text-xs font-medium text-warning">feed down — last known price</span>
      )}
    </div>
  );
}

/** The live INDEX beside the live premium, with how far it has drifted from
 * the index the trade entered at.
 *
 * Operator, 2026-09-29: *"should have nifty price along with option"*. The
 * drift is shown only when the engine stamped `signal_entry` — it is never
 * inferred from the premium, which moves on volatility and decay too. */
function SpotBadge({
  trade,
  spot,
}: Readonly<{ trade: TradeLogEntry; spot: number | null }>): ReactElement | null {
  if (spot === null) return null;
  const drift = trade.signalEntry !== null ? spot - trade.signalEntry : null;
  return (
    <span className="font-mono text-sm text-ink-secondary">
      {underlyingOf(trade.contractSymbol) ?? "index"} {formatIndex(spot)}
      {drift !== null && ` (${signedPoints(drift)})`}
    </span>
  );
}

function signOf(v: number): string {
  if (v > 0) return "+";
  if (v < 0) return "-";
  return "";
}

/** "+12.40" / "-3.05" / "0.00" — sign always explicit for points. */
function signedPoints(v: number): string {
  return `${signOf(v)}${Math.abs(v).toFixed(2)}`;
}

/** "+Rs 657" / "-Rs 120" — sign BEFORE the currency, never dropped. */
function signedRupees(v: number): string {
  return `${signOf(v)}Rs ${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
